<template>
  <div class="ab-layout">
    <el-alert
      v-if="localOnly"
      title="已开启“不同步对端”"
      description="AB 文件上传只处理本端服务器，不会再同步另一台服务器。调试完成后请记得关闭此开关。"
      type="warning"
      show-icon
      :closable="false"
      class="local-only-alert"
    />

    <div class="workspace-grid two-cols">
      <el-card class="workspace-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <span>AB 文件上传</span>
            <el-switch
              v-model="localOnly"
              :disabled="abUploading"
              active-text="不同步对端"
              title="打开后：AB 上传只处理本端服务器，不同步另一台服务器"
            />
          </div>
        </template>

        <el-upload
          ref="abUploadRef"
          class="upload-block"
          :auto-upload="false"
          :limit="1"
          :on-change="handleAbFileChange"
          :show-file-list="true"
          accept=".ab"
        >
          <template #trigger>
            <el-button type="primary">选择 .ab 文件</el-button>
          </template>
          <el-button class="ml-12" @click="clearAbFile">清空</el-button>
        </el-upload>

        <div class="info-block">
          <div class="info-row">
            <span class="info-label">当前文件</span>
            <span class="info-value">{{ abFile ? abFile.name : '未选择文件' }}</span>
          </div>
          <div v-if="abFile" class="info-row">
            <span class="info-label">VIN</span>
            <span class="info-value">{{ abVin || '无法识别' }}</span>
          </div>
          <div v-if="abFile" class="info-row">
            <span class="info-label">文件大小</span>
            <span class="info-value">{{ formatFileSize(abFile.size || 0) }}</span>
          </div>
        </div>

        <div class="tips-box">
          <div class="tip-line">1. 仅允许上传 .ab 文件。</div>
          <div class="tip-line">2. 文件名去掉 .ab 后即作为 VIN。</div>
          <div class="tip-line">3. 文件全部内容会原样写入 SQL 的 content 字段。</div>
        </div>

        <div class="action-row">
          <el-button type="primary" :loading="abUploading" @click="submitAbUpload">提交上传</el-button>
          <el-button :disabled="abUploading" @click="clearAbFile">取消</el-button>
          <el-button :loading="abListLoading" @click="refreshAbList">刷新列表</el-button>
        </div>
      </el-card>

      <el-card class="workspace-card" shadow="hover">
        <template #header>
          <div class="card-header"><span>上传结果</span></div>
        </template>

        <div v-if="abUploadResult" class="result-panel">
          <div class="result-row"><span>状态</span><strong>{{ abUploadResult.success ? '成功' : '失败' }}</strong></div>
          <div class="result-row"><span>消息</span><strong>{{ abUploadResult.message || '-' }}</strong></div>
          <div class="result-row"><span>文件名</span><strong>{{ abUploadResult.fileName || '-' }}</strong></div>
          <div class="result-row"><span>VIN</span><strong>{{ abUploadResult.vin || '-' }}</strong></div>
          <div class="result-row"><span>XML 长度</span><strong>{{ abUploadResult.xmlLength ?? '-' }}</strong></div>
          <div class="result-row"><span>是否新增</span><strong>{{ abUploadResult.inserted === true ? '是' : (abUploadResult.inserted === false ? '否' : '-') }}</strong></div>
        </div>
        <div v-else class="empty-result">暂无上传结果</div>
      </el-card>
    </div>

    <el-card class="workspace-card ab-list-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <span>AB 数据列表</span>
          <div class="header-actions">
            <el-input
              v-model="abKeyword"
              placeholder="输入 VIN 关键字"
              clearable
              class="ab-search-input"
              @keyup.enter="handleAbSearch"
              @clear="handleAbSearch"
            />
            <el-button type="primary" :loading="abListLoading" @click="handleAbSearch">查询</el-button>
          </div>
        </div>
      </template>

      <div class="table-scroll-shell">
        <div class="table-scroll-inner ab-table-scroll-inner">
          <el-table
            :data="abItems"
            stripe
            border
            class="result-table"
            v-loading="abListLoading"
            empty-text="暂无 AB 数据"
          >
            <el-table-column prop="id" label="ID" width="90" />
            <el-table-column prop="vin" label="VIN" min-width="320" />
            <el-table-column prop="xmlLength" label="XML 长度" width="120" />
            <el-table-column prop="contentPreview" label="内容预览" min-width="360" show-overflow-tooltip />
            <el-table-column label="操作" width="140" align="center" class-name="action-column">
              <template #default="scope">
                <el-button type="primary" size="small" class="view-content-btn" @click="showAbContent(scope.row)">查看内容</el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="pagination-wrap ab-pagination-wrap">
            <div class="ab-pagination-left">共 {{ abTotal }} 条</div>
            <div class="ab-pagination-right">
              <span class="pager-label">每页</span>
              <el-select v-model="abPageSize" class="page-size-select" @change="handleAbSizeChange">
                <el-option :value="10" label="10 条" />
                <el-option :value="20" label="20 条" />
                <el-option :value="50" label="50 条" />
                <el-option :value="100" label="100 条" />
              </el-select>
              <el-pagination
                background
                layout="prev, pager, next"
                :total="abTotal"
                :current-page="abPage"
                :page-size="abPageSize"
                @current-change="handleAbPageChange"
              />
              <span class="pager-label">跳至</span>
              <el-input v-model="abJumpPage" class="jump-input" @keyup.enter="handleAbJump" />
              <span class="pager-label">页</span>
              <el-button @click="handleAbJump">确定</el-button>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <el-dialog v-model="abContentDialogVisible" title="查看 AB XML 内容" width="82%">
      <div class="ab-detail-meta">
        <div class="result-row"><span>VIN</span><strong>{{ abDetailRow.vin || '-' }}</strong></div>
        <div class="result-row"><span>文件名</span><strong>{{ abDetailRow.fileName || '-' }}</strong></div>
        <div class="result-row"><span>XML 长度</span><strong>{{ abDetailRow.xmlLength ?? '-' }}</strong></div>
        <div class="result-row"><span>读取方式</span><strong>{{ abDetailLoading ? '正在请求完整内容...' : '已读取完整内容' }}</strong></div>
      </div>
      <div v-loading="abDetailLoading">
        <el-input
          v-model="abDetailContent"
          type="textarea"
          :rows="18"
          resize="none"
          readonly
          class="ab-detail-textarea"
        />
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="abContentDialogVisible = false">关闭</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { toRef } from 'vue';
import { useMazdaAb } from '@/composables/mazda/useMazdaAb';

const props = defineProps({
  serverName: { type: String, required: true },
  mazdaPost: { type: Function, required: true },
});

const {
  abFile,
  abVin,
  localOnly,
  abUploadResult,
  abItems,
  abTotal,
  abPage,
  abPageSize,
  abKeyword,
  abJumpPage,
  abUploading,
  abListLoading,
  abContentDialogVisible,
  abDetailRow,
  abDetailContent,
  abDetailLoading,
  abUploadRef,
  formatFileSize,
  handleAbFileChange,
  clearAbFile,
  submitAbUpload,
  refreshAbList,
  handleAbSearch,
  handleAbPageChange,
  handleAbSizeChange,
  handleAbJump,
  showAbContent,
} = useMazdaAb(props.mazdaPost, toRef(props, 'serverName'));
</script>

<style scoped>
.ab-layout {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.local-only-alert {
  border-radius: 14px;
}

.workspace-grid.two-cols {
  display: grid;
  grid-template-columns: repeat(2, minmax(340px, 1fr));
  gap: 22px;
}

.workspace-card,
.ab-list-card,
.query-card {
  border-radius: 24px;
  border: 1px solid rgba(225, 232, 242, 0.95);
  box-shadow: 0 12px 30px rgba(29, 46, 76, 0.07);
}

.workspace-card :deep(.el-card__body),
.ab-list-card :deep(.el-card__body),
.query-card :deep(.el-card__body) {
  padding-top: 16px;
  padding-bottom: 16px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-weight: 800;
  color: #1f2d3d;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.upload-block {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 6px 0 4px;
}

.upload-block :deep(.el-button),
.header-actions :deep(.el-button),
.path-actions :deep(.el-button),
.action-row :deep(.el-button),
.toolbar-actions :deep(.el-button),
.dialog-footer :deep(.el-button) {
  min-width: 96px;
}

.ml-12 {
  margin-left: 12px;
}

.info-block {
  margin-top: 18px;
  padding: 16px 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #fbfcff, #f6f9ff);
  border: 1px solid #ecf2ff;
}

.info-row,
.result-row {
  display: flex;
  gap: 12px;
  line-height: 1.9;
}

.info-label,
.result-row span:first-child {
  width: 90px;
  color: #8a94a6;
  flex-shrink: 0;
}

.info-value,
.result-row strong {
  color: #2f3441;
  word-break: break-all;
}

.path-box {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.path-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.tips-box {
  margin-top: 18px;
  padding: 16px 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #fffaf0, #fff7e6);
  border: 1px solid #ffe4ad;
}

.tip-line {
  line-height: 1.9;
  color: #7a5600;
}

.action-row {
  display: flex;
  gap: 12px;
  margin-top: 22px;
  flex-wrap: wrap;
}

.result-panel {
  min-height: 150px;
  padding: 16px 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #f7fbff, #f4f8ff);
  border: 1px solid #e8f1ff;
}

.empty-result {
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  border-radius: 16px;
  background: #fafafa;
  border: 1px dashed #dcdfe6;
}

.result-table,
.detail-table {
  width: 100%;
}

.result-table :deep(.el-table__inner-wrapper::before) {
  height: 0;
}


.table-scroll-shell {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  padding-bottom: 4px;
}

.table-scroll-inner {
  min-width: 100%;
}

.ab-table-scroll-inner {
  min-width: 1080px;
}

.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.ab-pagination-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.ab-pagination-left {
  color: #606266;
  font-weight: 600;
}

.ab-pagination-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.page-size-select {
  width: 110px;
}

.jump-input {
  width: 72px;
}

.pager-label {
  color: #606266;
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.ab-detail-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
  padding: 16px 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #fbfcff, #f6f9ff);
  border: 1px solid #ecf2ff;
}

.ab-detail-textarea :deep(.el-textarea__inner) {
  font-family: Consolas, "Courier New", monospace;
  line-height: 1.65;
  border-radius: 18px;
}


:deep(.action-column .cell) {
  overflow: visible;
  text-overflow: clip;
  white-space: nowrap;
  padding-left: 8px;
  padding-right: 8px;
}


.view-content-btn {
  min-width: 96px;
  font-weight: 700;
}

.view-content-btn:hover,
.view-content-btn:focus {
  color: #ffffff !important;
}

@media (max-width: 1180px) {
  .workspace-grid.two-cols {
    grid-template-columns: 1fr;
  }

  .toolbar-actions,
  .header-actions,
  .pagination-wrap,
  .ab-pagination-right {
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .ab-detail-meta {
    grid-template-columns: 1fr;
  }
}


.ab-search-input {
  width: 280px;
}

@media (max-width: 1180px) {
  .ab-search-input {
    width: 100%;
  }
}
</style>
